
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import ModalContent from '@/components/modal/ModalContentNew.vue';
import UPDATE_PROJECT_ACTIVITY from "@/apollo/projectActivities/UpdateActivity.gql";
import ModalFullScreen from '@/components/modal/ModalFullScreenNew.vue';
import VisitsTable from '@/components/projects/VisitsTable.vue';
import UIButton from "@/components/button/ui-button.vue";
import { Project, Project_Activity, User } from '@/generated/graphql';
import { NOTIFICATION } from "@/entities/Events";
import Message, { MessageDuration } from "@/data/notificationsMessages";
import getAuthData from "@/utils/getAuthData";

enum MODE {
    ACCEPT = "ACCEPT",
    REJECT = "REJECT",
}

@Component({
    components: {
        ModalFullScreen,
        ModalContent,
        VisitsTable,
        UIButton
    }
})

export default class ClientVisitsConfirmModal extends Vue {
    @Prop({ type: Array, required: false }) readonly selectedVisits: Project_Activity[] | null;
    @Prop({ type: String, required: true }) readonly currentMode: MODE;
    @Prop({ type: Object, required: true }) readonly currentUser!: User;

      statusProcessing: boolean = false;

    get modalTitle() {
        if (this.currentMode === MODE.ACCEPT) {
            return `Accept visits`;
        } else if (this.currentMode === MODE.REJECT) {
            return `Decline visits`;
        }
    }
 get modalType() {
        if (this.currentMode === MODE.ACCEPT) {
            return `accept`;
        } else if (this.currentMode === MODE.REJECT) {
            return `decline`;
        }
    }
    get buttonText() {
        if (this.currentMode === MODE.ACCEPT) {
            return "Accept";
        } else if (this.currentMode === MODE.REJECT) {
            return "Decline";
        }
    }

    async onSubmit() {
        this.statusProcessing = true;
        if (this.currentMode === MODE.ACCEPT) {
            if (this.selectedVisits.length > 0) {
                const allPromises = [];
                this.selectedVisits.forEach(visit => {
                    allPromises.push(this.updateBasicActivityData(visit.id, { accepted: true }));
                });
                try {
                    const res = await Promise.all(allPromises);
                    if (res.length > 0) {
                        const emailPromises = [];
                        res.forEach(({ data: { update_project_activity_by_pk: activity } }) => {
                            emailPromises.push(this.triggerAcceptRejectVisitEmail(this.currentMode, activity));
                        });
                        await Promise.all(emailPromises);
                         this.$eventBus.emit(
                            NOTIFICATION,
                            Message.successMessage(`You’ve successfully accepted ${res.length} visits. The manager will be notified.`, MessageDuration.DEFAULT, "Visits accepted")
                        );
                        this.closeModal();
                        window.location.reload();
                    }

                } catch (error) {
                    console.error(error);
                }
            }
        } else if (this.currentMode === MODE.REJECT) {
            if (this.selectedVisits.length > 0) {
                const allPromises = [];
                this.selectedVisits.forEach(visit => {
                    allPromises.push(this.updateBasicActivityData(visit.id, { accepted: false }));
                });
                try {
                    const res = await Promise.all(allPromises);
                    if (res.length > 0) {
                        const emailPromises = [];
                        const declinedVisitMatchesPromises = [];
                        
                        res.forEach(({ data: { update_project_activity_by_pk: activity } }) => {
                            emailPromises.push(this.triggerAcceptRejectVisitEmail(this.currentMode, activity));
                            // Trigger declined visit matches lambda for each declined visit
                            declinedVisitMatchesPromises.push(this.recordDeclinedVisitMatches(activity.id));
                        });
                        
                        // Run email notifications and declined visit matches in parallel
                        await Promise.all([
                            Promise.all(emailPromises),
                            Promise.all(declinedVisitMatchesPromises)
                        ]);
                        
                        this.$eventBus.emit(
                            NOTIFICATION,
                            Message.successMessage(`You've successfully declined ${res.length} visits. The manager will be notified.`, MessageDuration.DEFAULT, "Visits have been declined")
                        );
                        this.closeModal();
                         window.location.reload();
                    }

                } catch (error) {
                    console.error(error);
                }
            }
        }
    }

    async updateBasicActivityData(id: string, activity: Partial<Project_Activity>) {
        return await this.$apollo.mutate({
            mutation: UPDATE_PROJECT_ACTIVITY,
            variables: {
                id,
                set: activity,
            },
        });
    }

    async triggerAcceptRejectVisitEmail(currentMode, activity) {
        const uri = `${process.env.VUE_APP_LAMBDA}/send-visit-accept-reject-email`;
        const { headers } = await getAuthData();
        const { data }: any = await this.axios.post(uri, {
            data: {
                activity,
                currentMode
            },
        }, { headers }
        );
    }

    async recordDeclinedVisitMatches(projectActivityId: string) {
        try {
            const { headers } = await getAuthData();
            
            console.debug(`📤 Sending declined visit matches request - project_activity_id: ${projectActivityId}, user_id: ${this.currentUser.id}`);
            
            const uri = `${process.env.VUE_APP_LAMBDA}/record-declined-visit-matches`;
            const { data }: any = await this.axios.post(uri, {
                project_activity_id: projectActivityId,
                user_id: this.currentUser.id
            }, { headers });
            
            console.debug(`✅ Successfully recorded declined visit matches for activity: ${projectActivityId}`, data);
            return data;
            
        } catch (error) {
            console.error(`❌ Error recording declined visit matches for activity: ${projectActivityId}`, error);
            // Don't throw the error - we don't want to block the decline process if this fails
            return null;
        }
    }


    closeModal() {
        this.$emit('close');
        this.statusProcessing = false;
    }

}
